import { MapPin, Xmark } from '@byway/icons';
import clsx from 'clsx';
import Fuse, { type IFuseOptions } from 'fuse.js';
import { useLocale, useTranslations } from 'next-intl';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Collection, Header, ListBoxSection } from 'react-aria-components';
import { useDebounce } from 'use-debounce';
import { Button } from '~/components/Button';
import { Combobox, ComboboxItem, ComboboxItemIcon } from '~/components/Combobox';
import type { Locale } from '~/i18n/i18n-config';
import { api } from '~/libs/api';
import { trackEvent } from '~/utils';
import { defaultFuseOptions } from '../PlaygroundDestinationSelector/PlaygroundDestinationSelector';
import styles from './PlaygroundStartEndSelector.module.css';
function useFuzzySearch<T>(items: Array<T>, filterValue: string, options: IFuseOptions<T> = {
  ...defaultFuseOptions,
  keys: ['n', 'c.n']
}) {
  const fuse = useMemo(() => new Fuse(items, {
    keys: options.keys,
    threshold: options.threshold,
    ignoreLocation: options.ignoreLocation,
    ignoreDiacritics: options.ignoreDiacritics
  }), [items]);
  return fuse.search(filterValue).map(item => item.item);
}
type PlaygroundStartEndSelectorProps = {
  value?: any;
  onChange?: (...event: any[]) => void;
  onBlur?: () => void;
  name?: string;
  variant: 'start' | 'end';
};
export const PlaygroundStartEndSelector = forwardRef<HTMLDivElement, PlaygroundStartEndSelectorProps>((props, ref) => {
  const {
    name,
    value,
    onChange,
    variant
  } = props;
  const t = useTranslations();
  const [filterValue, setFilterValue] = useState('');
  const [shouldAutoFocus, setShouldAutoFocus] = useState(false);
  const [debouncedFilterValue] = useDebounce(filterValue, 200);
  const popoverRef = useRef<HTMLDivElement>(null);
  const locale = useLocale() as Locale;
  // Fetched here rather than read from the playground store: the store is only seeded on /map/build,
  // so it is empty on /map and the home page hero.
  const {
    data: allCmsPlaces = []
  } = api.cms.places.useQuery({
    locale
  }, {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity
  });

  // The DP engine has no locale of its own, so origin/end place names always come back in
  // English. allCmsPlaces is fetched in the current locale, so we swap in its place/country
  // names for display wherever a DP place has a matching CMS record (matched by placeId).
  const cmsPlaceByPlaceId = useMemo(() => new Map(allCmsPlaces.map(place => [place.placeId, place])), [allCmsPlaces]);
  const localisePlace = (item: PlaygroundJaiOriginItem): PlaygroundJaiOriginItem => {
    const cmsPlace = cmsPlaceByPlaceId.get(item.id);
    if (!cmsPlace) return item;
    return {
      ...item,
      n: cmsPlace.placeName ?? item.n,
      c: {
        n: cmsPlace.country?.countryName ?? item.c?.n
      }
    };
  };

  // Keep API call commented out for future use
  const {
    data: origins
  } = api.playground.origins.useQuery(undefined, {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  });
  // Search and sort against the localised names so e.g. "Praag" matches Prague in nl-NL; the
  // place id is preserved through localisePlace, so selection still resolves against the
  // canonical (English) DP record below.
  const localisedOrigins = useMemo(() => (origins ?? []).map(localisePlace), [origins, cmsPlaceByPlaceId]);
  const filteredOrigins = useFuzzySearch(localisedOrigins, debouncedFilterValue);

  // Scroll to top whenever the debounced filter value (i.e. filtered dropdown results) change
  useEffect(() => {
    if (popoverRef.current && debouncedFilterValue) {
      popoverRef.current.scrollTo({
        top: 0
      });
    }
  }, [debouncedFilterValue]);
  const handleFocus = () => {
    setShouldAutoFocus(false);
    if (variant === 'end') {
      trackEvent('end_place_search', {
        event_category: 'Playground',
        event_label: 'end place search click'
      });
    }
    if (variant === 'start') {
      trackEvent('origin_search', {
        event_category: 'Playground',
        event_label: 'origin search click'
      });
    }
  };
  const allSortedOrigins = localisedOrigins.slice().sort((a, b) => {
    const results = a.n.localeCompare(b.n, undefined, {
      sensitivity: 'base'
    });
    return results;
  });
  const noResults = {
    id: -9999,
    // special ID for no results
    n: t('content.feedback.search_generic_empty'),
    c: {
      n: ''
    }
  };
  type PlaygroundJaiOriginItem = {
    id: number;
    n: string;
    c: {
      n: string;
    };
  };
  type PlaygroundPlaceSection = {
    id: 'origins' | 'end-places';
    name: string;
    items: PlaygroundJaiOriginItem[] | undefined;
  };
  const originSections: PlaygroundPlaceSection[] = useMemo<PlaygroundPlaceSection[]>(() => [{
    id: variant === 'start' ? 'origins' : 'end-places',
    name: variant === 'start' ? t('playground.origins_list_heading') : t('playground.end_places_list_heading'),
    items: filteredOrigins.length > 0 ? filteredOrigins : debouncedFilterValue ? [noResults] : allSortedOrigins
  }], [filteredOrigins]) ?? [];
  const hasValue = value?.id;
  const handleClearSelection = () => {
    onChange?.(null, undefined);
    setFilterValue('');
    setShouldAutoFocus(true);
  };
  return <div className={styles.comboboxContainer} ref={ref}>
      {!hasValue ? <Combobox name={name ?? 'origin-selector'} inputValue={filterValue} onInputChange={setFilterValue} aria-labelledby={name ?? 'origin-selector'} placeholder={variant === 'start' ? t('playground.origin_select') : t('playground.destination')} items={originSections} allowsEmptyCollection={true} autoFocus={shouldAutoFocus} onFocus={handleFocus} onSelectionChange={selectedId => {
      if (!selectedId || selectedId === -9999) {
        onChange?.(null, undefined);
        return;
      }
      // Find the full object from the selected ID
      const selectedItem = origins?.find(item_0 => item_0.id === Number(selectedId));
      if (selectedItem) {
        onChange?.(selectedItem, undefined);
      }
    }} hasButton popoverRef={popoverRef}>
          {(section: PlaygroundPlaceSection) => {
        return <ListBoxSection className={styles.section} id={section.id}>
                <Header className={styles.label}>{section.name}</Header>
                <Collection items={section.items ?? []}>
                  {(item_1: PlaygroundJaiOriginItem) => {
              const isNoResults = item_1.id === -9999;
              return <ComboboxItem textValue={item_1.n} id={item_1.id} key={item_1.id} isDisabled={isNoResults}>
                        {isNoResults ? <p className={clsx(styles.placeName, styles.grey)}>
                            {debouncedFilterValue ? t('content.feedback.search_generic_empty') : ''}
                          </p> : <div className={styles.pinAndTextContainer}>
                            <ComboboxItemIcon>
                              <MapPin size="large" decorative />
                            </ComboboxItemIcon>
                            <div>
                              <h2 className={styles.placeName}>{item_1.n}</h2>
                              <span className={styles.countryName}>{item_1.c.n}</span>
                            </div>
                          </div>}
                      </ComboboxItem>;
            }}
                </Collection>
              </ListBoxSection>;
      }}
        </Combobox> : <div onClick={handleClearSelection} className={styles.selectedValueContainer}>
          <div className={styles.inputContainer}>
            <div className={styles.selectedValue}>
              <h2 className={styles.placeName}>{`${localisePlace(value).n}, `}</h2>
              <span className={styles.countryName}>{localisePlace(value).c?.n}</span>
            </div>
            <Button ariaLabel={t('aria.selection_clear')} variant="icon" onPress={handleClearSelection}>
              <Xmark size="large" decorative />
            </Button>
          </div>
        </div>}
    </div>;
});
PlaygroundStartEndSelector.displayName = 'PlaygroundStartEndSelector';